/**
 * JST ライセンス期限ユーティリティ（フロント用ミラー）
 * サーバー正本: server/utils/jstDateHelper.js
 */

import { LICENSE_USAGE_CUTOFF_HOUR_JST } from '../config/constants.js';

const JST = 'Asia/Tokyo';

export { LICENSE_USAGE_CUTOFF_HOUR_JST };

export function getJstYmd(ms) {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));
  const pick = (type) => parts.find((p) => p.type === type)?.value;
  return {
    y: parseInt(pick('year'), 10),
    m: parseInt(pick('month'), 10),
    d: parseInt(pick('day'), 10),
  };
}

export function jstDateTimeMs(y, m, d, hour = 0, min = 0, sec = 0, msVal = 0) {
  return Date.UTC(y, m - 1, d, hour - 9, min, sec, msVal);
}

export function addJstCalendarDays(y, m, d, days) {
  const anchor = jstDateTimeMs(y, m, d, 12, 0, 0, 0);
  return getJstYmd(anchor + days * 86400000);
}

function compareJstYmd(a, b) {
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
}

export function cutoffMsAfterLastUsageDay(y, m, d) {
  const next = addJstCalendarDays(y, m, d, 1);
  return jstDateTimeMs(next.y, next.m, next.d, LICENSE_USAGE_CUTOFF_HOUR_JST, 0, 0, 0);
}

export function cutoffMsFromStartAndDays(startMs, days) {
  const start = getJstYmd(startMs);
  const last = addJstCalendarDays(start.y, start.m, start.d, days);
  return cutoffMsAfterLastUsageDay(last.y, last.m, last.d);
}

export function cutoffMsForJstMonthEnd(fromMs) {
  const { y, m } = getJstYmd(fromMs);
  const lastD = getJstYmd(jstDateTimeMs(y, m + 1, 0, 12, 0, 0, 0)).d;
  return cutoffMsAfterLastUsageDay(y, m, lastD);
}

export function lastUsageDayMsFromCutoff(cutoffMs) {
  if (!cutoffMs) return null;
  const { y, m, d } = getJstYmd(cutoffMs);
  const last = addJstCalendarDays(y, m, d, -1);
  return jstDateTimeMs(last.y, last.m, last.d, 12, 0, 0, 0);
}

export function normalizeExpiryToCutoffMs(expiryMs) {
  if (!expiryMs) return null;
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: JST,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  }).formatToParts(new Date(expiryMs));
  const pick = (type) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10);
  if (pick('hour') === LICENSE_USAGE_CUTOFF_HOUR_JST && pick('minute') === 0 && pick('second') === 0) {
    return expiryMs;
  }
  const { y, m, d } = getJstYmd(expiryMs);
  return cutoffMsAfterLastUsageDay(y, m, d);
}

function isBeforeCutoff(nowMs, cutoffMs) {
  if (!cutoffMs) return true;
  return nowMs < cutoffMs;
}

function isOnOrBeforeLastUsageDay(nowMs, cutoffOrLegacyMs) {
  if (!cutoffOrLegacyMs) return true;
  if (isBeforeCutoff(nowMs, cutoffOrLegacyMs)) return true;
  const nowDay = getJstYmd(nowMs);
  const lastDay = getJstYmd(lastUsageDayMsFromCutoff(normalizeExpiryToCutoffMs(cutoffOrLegacyMs)));
  return compareJstYmd(nowDay, lastDay) <= 0;
}

export function isExpiryUsable(nowMs, expiryMs) {
  if (!expiryMs) return true;
  if (isBeforeCutoff(nowMs, expiryMs)) return true;
  return isOnOrBeforeLastUsageDay(nowMs, expiryMs);
}

export function formatJstDate(ms) {
  return new Date(ms).toLocaleDateString('ja-JP', { timeZone: JST });
}

export function formatLastUsageDayJa(cutoffMs) {
  const lastMs = lastUsageDayMsFromCutoff(cutoffMs);
  return lastMs ? formatJstDate(lastMs) : '';
}

export function getNextBillingAnchorMs(baseMs = Date.now()) {
  const { y, m } = getJstYmd(baseMs);
  let by = y;
  let bm = m + 1;
  if (bm > 12) {
    bm = 1;
    by += 1;
  }
  return jstDateTimeMs(by, bm, 1, LICENSE_USAGE_CUTOFF_HOUR_JST, 0, 0, 0);
}

export function getNextBillingAnchorDate(baseMs = Date.now()) {
  return new Date(getNextBillingAnchorMs(baseMs));
}

export function diffCalendarDaysJst(fromMs, toMs) {
  const a = getJstYmd(fromMs);
  const b = getJstYmd(toMs);
  const da = Date.UTC(a.y, a.m - 1, a.d);
  const db = Date.UTC(b.y, b.m - 1, b.d);
  return Math.round((db - da) / 86400000);
}

export function lastUsageDayDateFromCutoff(cutoffMs) {
  const ms = lastUsageDayMsFromCutoff(cutoffMs);
  return ms ? new Date(ms) : null;
}
